import { useMediaQuery, useTheme, Popper, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import React, { useState } from 'react';

import ButtonsField from '../../../../../FMForm/FormTypes/ButtonsField/ButtonsField';
import CheckboxInput from '../../../../../FMForm/FormTypes/CheckboxInput/CheckboxInput';
import DateTimeInput from '../../../../../FMForm/FormTypes/DateTimeInput/DateTimeInput';
import FileUploadField from '../../../../../FMForm/FormTypes/FileUploadField/FileUploadField';
import LookupInput from '../../../../../FMForm/FormTypes/LookupInput/LookupInput';
import RadioInput from '../../../../../FMForm/FormTypes/RadioInput/RadioInput';
import SelectInput from '../../../../../FMForm/FormTypes/SelectInput/SelectInput';
import SwitchInput from '../../../../../FMForm/FormTypes/SwitchInput/SwitchInput';
import TextInput from '../../../../../FMForm/FormTypes/TextInput/TextInput';
import { useSelector } from 'react-redux';


const PopOverBlock = styled(Popper)(() => ({
  pointerEvents: 'none',
  padding: '5px',
  background: '#e5453b',
  color: 'white',
  zIndex: 10,
}));


const FieldGenerator = ({ field, options, disableFormSubmit, ...props }) => {
  const modifiedField = { ...field };
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = useState(null);
  const [activeLookupId, setActiveLookupId] = useState(null);

  const addManuallyRow = useSelector((state) => state?.filters?.addManuallyRow);

  const handlePopoverOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handlePopoverClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);
  const largeScreen = useMediaQuery(theme.breakpoints.up('sm'));

  const isBoolean = (val) => typeof val === 'boolean';

  const helperText = isBoolean(modifiedField.validationResult) && !modifiedField.validationResult
    ? modifiedField.validationMessage
    : modifiedField.info;

  if (helperText) {
    modifiedField.inline = true;
  };

  let component = null;
  if (!modifiedField.hidden) {

    switch (modifiedField.type) {
      case 'text_field':
      case 'number':
      case 'text_area':
        component = (
          <TextInput
            {...modifiedField}
            readOnly={options?.readOnly ? true : modifiedField.readOnly}
            isSmallScreen={!largeScreen}
            islabelvisible
            {...props}
          />
        );
        break;

      case 'radiobutton':
        component = (
          <RadioInput
            {...modifiedField}
            readOnly={options?.readOnly ? true : modifiedField.readOnly}
            isSmallScreen={!largeScreen}
            {...props}
          />
        );
        break;

      case 'dropdown':
        component = (
          <SelectInput
            {...modifiedField}
            readOnly={options?.readOnly ? true : modifiedField.readOnly}
            isSmallScreen={!largeScreen}
            islabelvisible
            multiple={false}
            {...props}
          />
        );
        break;

      case 'multi_selection':
        component = (
          <SelectInput
            {...modifiedField}
            readOnly={options?.readOnly ? true : modifiedField.readOnly}
            isSmallScreen={!largeScreen}
            multiple
            islabelvisible
            {...props}
          />
        );
        break;

      case 'file_field':
        component = (
          <FileUploadField
            {...modifiedField}
            {...props}
            readOnly={options?.readOnly ? true : modifiedField.readOnly}
            isSmallScreen={!largeScreen}
            addManuallyRow={addManuallyRow}
          />
        );
        break;

      case 'buttons_field':
        component = (
          <ButtonsField
            {...modifiedField}
            readOnly={options?.readOnly ? true : modifiedField.readOnly}
            isSmallScreen={!largeScreen}
            {...props}
          />
        );
        break;

      case 'switch':
        component = (
          <SwitchInput
            {...modifiedField}
            readOnly={options?.readOnly ? true : modifiedField.readOnly}
            isSmallScreen={!largeScreen}
            {...props}
          />
        );
        break;

      case 'checkbox':
        component = (
          <CheckboxInput
            {...modifiedField}
            readOnly={options?.readOnly ? true : modifiedField.readOnly}
            isSmallScreen={!largeScreen}
            {...props}
          />
        );
        break;

      case 'time':
      case 'datetime':
        component = (
          <DateTimeInput
            {...modifiedField}
            readOnly={options?.readOnly ? true : modifiedField.readOnly}
            isSmallScreen={!largeScreen}
            islabelvisible
            disableFormSubmit={disableFormSubmit}
            handleBlurDate={props.handleBlurDate}
            handleBlurDateTime={props.handleBlurDateTime}
            handleChange={props.handleChange}
            handleChangeCheck={props.handleChangeCheck}
            handleChangeData={props.handleChangeData}
            handleChangeDateTime={props.handleChangeDateTime}
            handleChangeSwitch={props.handleChangeSwitch}
            handleClick={props.handleClick}
          />
        );
        break;

      case 'date':
        component = (
          <DateTimeInput
            {...modifiedField}
            readOnly={options?.readOnly ? true : modifiedField.readOnly}
            isSmallScreen={!largeScreen}
            islabelvisible
            disableFormSubmit={disableFormSubmit}
            handleBlurDate={props.handleBlurDate}
            handleBlurDateTime={props.handleBlurDateTime}
            handleChange={props.handleChange}
            handleChangeCheck={props.handleChangeCheck}
            handleChangeData={props.handleChangeData}
            handleChangeDateTime={props.handleChangeDateTime}
            handleChangeSwitch={props.handleChangeSwitch}
            handleClick={props.handleClick}
            handleChangeTime={props.handleChangeTime}
            handleBlurTime={props.handleBlurTime}
          />
        );
        break;

      case 'password_field': // TODO
        break;
      case 'slider': // TODO
        break;
      case 'image_field': // TODO
        break;
      case 'lookup':
        component = (
          <LookupInput
            {...modifiedField}
            readOnly={options?.readOnly ? true : modifiedField.readOnly}
            isSmallScreen={!largeScreen}
            islabelvisible
            activeLookupId={activeLookupId}
            setActiveLookupId={setActiveLookupId}
            {...props}
          />
        );
        break;
      default:
        break;
    }
  }

  if (helperText) {
    component = [
      <span
        key='test'
        aria-haspopup='true'
        onMouseEnter={handlePopoverOpen}
        onMouseLeave={handlePopoverClose}>
        {component}
      </span>,
      <PopOverBlock
        key='test'
        id={modifiedField.index}
        placement='top'
        disablePortal
        open={open}
        anchorEl={anchorEl}
        onClose={handlePopoverClose}
        popperOptions={{ modifiers: { arrow: { enabled: true } } }}>
        <Typography>{helperText}</Typography>
      </PopOverBlock>,
    ];
  }

  return component;
};

export default React.memo(FieldGenerator);
